import { groupSummaryRows, summaryTrucks } from "./consolidationSummaryPresentation.js";
import { visitBilledParties } from "./consolidationBillingParty.js";
import { driverIdentity } from "./consolidationOverview.js";
import { workedSeconds } from "./consolidationPresentation.js";

const normalize = value => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase("fr").trim();
const CONFIGURE_CODES = new Set(["billing_mapping_required", "rate_missing"]);
const cents = rows => rows.filter(row => row.amount != null).reduce((sum, row) => sum + Math.round(Number(row.amount) * 100), 0);
const sumQuantity = (rows, unit) => rows.filter(row => row.unit === unit).reduce((sum, row) => sum + Number(row.quantity || 0), 0);
// Tons billed by dump trucks; a citerne's tonne supplement is not a billed tonnage.
const dompeurTons = rows => sumQuantity(rows.filter(row => row.charge?.kind === "dompeur"), "tonne");

// Routes and preloads not validated yet, by Coop: their prestations will join that card once validated.
export function pendingRoutesByPlace(days = []) {
  const places = new Map();
  const add = (name, entry) => {
    const key = normalize(name);
    if (!places.has(key)) places.set(key, { label: name, routes: [] });
    places.get(key).routes.push(entry);
  };
  for (const day of days) {
    // Préchargement non validé : l'envoi du relevé de transport de ses entreprises l'attend.
    for (const preload of (day.preloads || []).filter(row => row.status !== "validated")) {
      for (const name of new Set(visitBilledParties({ orders: preload.links }).names)) {
        add(name, { kind: "preload", date: day.date, employee: null, driver: `Préchargement · ${preload.employee_name || "employé non renseigné"}`,
          status: preload.id ? "draft" : "expected", seconds: null, planned: false });
      }
    }
    const routes = [...day.driver_groups.flatMap(group => group.routes.map(route => ({ route, employee: group.key, driver: group.driver_name }))),
      ...(day.unassigned_routes || []).map(route => ({ route, employee: null, driver: "Sans chauffeur identifié" }))];
    for (const { route, employee, driver } of routes) {
      if (route.sheet.status === "validated") continue;
      const names = new Set(visitBilledParties(route).names);
      for (const name of names) {
        const worked = workedSeconds(route.sheet);
        const planned = (Date.parse(route.sheet.planned_service_end) - Date.parse(route.sheet.planned_service_start)) / 1000;
        add(name, { kind: "sheet", date: day.date, employee, driver, truck: route.sheet.truck_number,
          trailer: route.sheet.dispatch_assignment?.compartment_name || null, status: route.sheet.id ? "draft" : "expected",
          seconds: worked ?? (Number.isFinite(planned) && planned > 0 ? planned : null), planned: worked == null });
      }
    }
  }
  return places;
}

export function countPendingRoutes(days = []) {
  return days.reduce((total, day) => total + [...day.driver_groups.flatMap(group => group.routes), ...(day.unassigned_routes || [])]
    .filter(route => route.sheet.status !== "validated").length, 0);
}

function coopState(rows, pending, configure) {
  const eligible = rows.some(row => row.status === "eligible");
  if (configure) return "configure";
  if (!rows.length) return "waiting";
  if (rows.every(row => row.status === "allocated")) return "invoiced";
  if (eligible) return rows.some(row => row.status === "blocked") || pending.length ? "partial" : "ready";
  return rows.some(row => row.status === "blocked") ? "blocked" : pending.length ? "waiting" : "invoiced";
}

export const sourceKey = row => `${row.source_object_id || row.source_revision_id || row.id}:${row.source_date || row.date}`;

// One line per relevé (or preload) of a coop: what « Détail par chauffeur » lists and selects.
export function coopSources(coop) {
  const sources = new Map();
  for (const row of coop.rows) {
    const key = sourceKey(row);
    if (!sources.has(key)) sources.set(key, { key, date: row.source_date || row.date, rows: [], employee: row.employee_id || row.driver_name || null,
      label: row.source_type === "preload" ? `Préchargement${row.driver_name ? ` · ${row.driver_name}` : ""}` : row.driver_name || "Chauffeur non renseigné",
      trucks: summaryTrucks(row) });
    sources.get(key).rows.push(row);
  }
  return [...sources.values()].map(source => ({ ...source,
    seconds: sumQuantity(source.rows, "heure") * 3600, tons: dompeurTons(source.rows),
    cents: source.rows.some(row => row.amount == null) ? null : cents(source.rows),
    status: source.rows.every(row => row.status === "allocated") ? "allocated" : source.rows.some(row => row.status === "blocked") ? "blocked" : "eligible",
  })).sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label, "fr"));
}

export function coopOverview(rows, days) {
  const pending = pendingRoutesByPlace(days);
  const matched = new Set();
  const coops = groupSummaryRows(rows).map(group => {
    const key = [normalize(group.mill.name), normalize(group.label)].find(name => pending.has(name));
    if (key) matched.add(key);
    return { ...group, pending: key ? pending.get(key).routes : [] };
  });
  for (const [key, place] of pending) {
    if (!matched.has(key)) coops.push({ key: `pending:${key}`, mill: { name: place.label }, label: place.label, rows: [], drivers: [], preloads: [], reasons: [], pending: place.routes });
  }
  return coops.map(coop => {
    const eligible = coop.rows.filter(row => row.status === "eligible");
    const configure = coop.rows.some(row => row.status === "blocked" && (row.blocking_reasons || []).some(reason => CONFIGURE_CODES.has(reason.code)));
    // « Tarif normal ($/h) » de l'entreprise de facturation, pas l'« Exception $/h » d'une commande ; d'abord une ligne à jour.
    // Sans heure facturée (dompeur), aucun tarif horaire n'est affiché.
    const normal = coop.rows.some(row => row.unit === "heure") ? (coop.rows.find(row => row.status !== "allocated" && row.mill?.normal_hourly_rate != null)
      || coop.rows.find(row => row.mill?.normal_hourly_rate != null))?.mill.normal_hourly_rate ?? null : null;
    // Un dompeur sans $/t n'a pas de tarif horaire à configurer : son motif renvoie aux Paramètres.
    const reasonCodes = new Set(coop.rows.flatMap(row => row.status === "blocked" ? (row.blocking_reasons || [])
      .map(reason => reason.code === "rate_missing" && row.charge?.kind === "dompeur" ? "dompeur_rate_missing" : reason.code) : []));
    return { ...coop, eligible, configure, state: coopState(coop.rows, coop.pending, configure), rate: normal, reasonCodes,
      account: coop.rows.find(row => row.billing_account_id && row.mill?.account_name)?.mill.account_name || null,
      readyCents: cents(eligible), knownCents: cents(coop.rows), unknownAmounts: coop.rows.some(row => row.amount == null),
      seconds: sumQuantity(coop.rows, "heure") * 3600, readySeconds: sumQuantity(eligible, "heure") * 3600, tons: dompeurTons(coop.rows),
      readySources: new Set(eligible.map(row => row.source_object_id || row.source_revision_id || row.id)).size,
      pendingCount: coop.pending.length };
  }).sort((a, b) => b.readyCents - a.readyCents || b.seconds - a.seconds || b.pendingCount - a.pendingCount || a.label.localeCompare(b.label, "fr"));
}
